import { cast, flow, SnapshotIn, toGenerator, types } from 'mobx-state-tree'

import {
  ChecklistReportsReportModel,
  ChecklistReportsReportSnapshot,
} from '../models/ChecklistReports/ChecklistReportModel'
import { ChecklistKind, checklistKindSelectOptions } from '../models/Checklists/ChecklistKind'
import { FetchModel } from '../models/FetchModel'
import { FilterModel } from '../models/common/Filters/FilterModel'
import { FilterType } from '../models/common/Filters/FilterType'
import { SelectFieldFilter, SelectFilterIcon } from '../models/common/Filters/SelectFieldFilter'
import {
  OperationFailure,
  OperationInProgress,
  OperationNotStarted,
  OperationStatusModel,
  OperationSuccess,
} from '../models/common/OperationStatus'
import { getDeploymentName } from './rootStore'

import type { ChecklistReportFilterValues } from '../models/common/Filters/FiltersValueModels'

const CHECKLIST_REPORT_FILTERS: SnapshotIn<typeof FilterModel> = {
  fieldFilters: {
    mission_id: { type: FilterType.SEARCH, placeholder: 'Mission ID', fieldNetworkName: 'mission_id' },
    date: { type: FilterType.DATE },
    user: { type: FilterType.SELECT, icon: SelectFilterIcon.User, fieldNetworkName: 'user_id' },
    checklistType: { type: FilterType.SELECT, icon: SelectFilterIcon.Checklist, fieldNetworkName: 'checklist_type' },
    vehicle: { type: FilterType.SELECT, icon: SelectFilterIcon.Vehicle, fieldNetworkName: 'vehicle_id' },
  },
}

export const ChecklistReportsStore = FetchModel.named('ChecklistReportsStore')
  .props({
    report: types.maybeNull(ChecklistReportsReportModel),

    fetchReportStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    fetchNextReportPageStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    fetchFilterOptionsStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    downloadPdfStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    lastFetchedReportPage: types.maybeNull(types.number),
    filters: types.optional(FilterModel, CHECKLIST_REPORT_FILTERS),
  })
  .views((self) => ({
    checklistReportsUrl(pageNumber: number): string {
      return `${self.oversightBaseUrl}checklist_report/?deployment=${encodeURIComponent(getDeploymentName())}&${self.filters.asUrlSearchParams}&page_size=10&page_number=${pageNumber}`
    },
    get checklistReportFilterValuesUrl(): string {
      return `${self.oversightBaseUrl}checklist_report/filter_data?deployment=${encodeURIComponent(getDeploymentName())}`
    },
  }))
  .views((self) => ({
    _getFilterByFieldName(fieldName: string) {
      const filter = self.filters.fieldFilters.get(fieldName)
      if (filter === undefined) {
        throw new Error(`filter ${fieldName} must appear`)
      }
      return filter
    },
  }))
  .views((self) => ({
    get _userFilter(): SelectFieldFilter {
      return self._getFilterByFieldName('user') as SelectFieldFilter
    },
    get _vehicleFilter(): SelectFieldFilter {
      return self._getFilterByFieldName('vehicle') as SelectFieldFilter
    },
    get _checklistTypeFilter(): SelectFieldFilter {
      return self._getFilterByFieldName('checklistType') as SelectFieldFilter
    },
  }))
  .actions((self) => ({
    setChecklistReportsReport(report: ChecklistReportsReportSnapshot) {
      self.report = cast(report)
    },
  }))
  .actions((self) => ({
    fetchAndSetChecklistReports: flow(function* fetchAndSetChecklistReports() {
      self.fetchReportStatus = OperationInProgress()
      self.lastFetchedReportPage = null
      const pageToFetch = 1
      const loadResult = yield* toGenerator(
        self.loadObjectOrError<ChecklistReportsReportSnapshot>(
          self.checklistReportsUrl(pageToFetch),
          {},
          { fetchOperationName: 'Fetch checklist report' },
        ),
      )
      if (loadResult.error) {
        self.fetchReportStatus = OperationFailure(loadResult.error)
        return
      }

      self.setChecklistReportsReport(loadResult.result)
      self.lastFetchedReportPage = 1
      self.fetchReportStatus = OperationSuccess()
    }),
    fetchNextReportPage: flow(function* fetchNextReportPage() {
      if (self.lastFetchedReportPage === null || self.report === null) {
        throw new Error("Can't fetch next page, no report was fetched")
      }
      self.fetchNextReportPageStatus = OperationInProgress()

      const loadResult = yield* toGenerator(
        self.loadObjectOrError<ChecklistReportsReportSnapshot>(
          self.checklistReportsUrl(self.lastFetchedReportPage + 1),
          {},
          { fetchOperationName: 'Fetch checklist report' },
        ),
      )
      if (loadResult.error) {
        self.fetchNextReportPageStatus = OperationFailure(loadResult.error)
        return
      }
      const checklistReports = loadResult.result.checklist_reports ?? []

      if (self.report.metadata.totalResults !== loadResult.result.metadata.total_results) {
        // The report could have changed even if the number of results didn't, but we don't have a way to tell for we ignore this issue for now.
        self.fetchNextReportPageStatus = OperationFailure({
          name: 'ReportChangedError',
          message: 'The report was changed since loading, please refresh the page',
        })
        return
      }

      self.report.checklist_reports.push(...checklistReports)
      self.report.metadata = cast(loadResult.result.metadata)
      self.lastFetchedReportPage = self.lastFetchedReportPage + 1
      self.fetchNextReportPageStatus = OperationSuccess()
    }),
    fetchAndSetFilterOptions: flow(function* fetchAndSetFilterOptions() {
      self.fetchFilterOptionsStatus = OperationInProgress()
      const loadResult = yield* toGenerator(
        self.loadObjectOrError<ChecklistReportFilterValues>(
          self.checklistReportFilterValuesUrl,
          { method: 'GET' },
          { fetchOperationName: 'Fetch checklist report filter values' },
        ),
      )
      if (loadResult.error) {
        self.fetchFilterOptionsStatus = OperationFailure(loadResult.error)
        return
      }

      self._userFilter.setOptions(
        loadResult.result.users.map((user) => ({
          value: user.rpic_user_id.toString(),
          text: user.rpic_user_name,
        })),
      )
      self._vehicleFilter.setOptions(
        loadResult.result.vehicles.map((vehicle) => ({
          value: vehicle.vehicle_id.toString(),
          text: vehicle.vehicle_name,
        })),
      )
      self._checklistTypeFilter.setOptions(
        checklistKindSelectOptions.filter((option) =>
          loadResult.result.checklist_types.includes(option.value as ChecklistKind),
        ),
      )
      self.fetchFilterOptionsStatus = OperationSuccess()
    }),
  }))